import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

const SHARE_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only visible to you',
    Icon: LockOutlinedIcon,
  },
  {
    value: 'project',
    label: 'Project',
    description: 'All project members',
    Icon: FolderOutlinedIcon,
  },
  {
    value: 'organization',
    label: 'Organization',
    description: 'All org members',
    disabledDescription: 'Only org admins & managers can share org-wide',
    Icon: BusinessOutlinedIcon,
  },
];

const ORG_ALLOWED_ROLES = ['org-admin', 'org-manager'];

const SavePresetPopover = ({
  open = false,
  anchorEl = null,
  onClose,
  onSave,
  saving = false,
  error = null,
  title = 'Save Filter',
  userRole = 'member',
}) => {
  const [name, setName] = useState('');
  const [shareLevel, setShareLevel] = useState('private');
  const inputRef = useRef(null);

  const canShareOrg = ORG_ALLOWED_ROLES.includes(userRole);

  useEffect(() => {
    if (open) {
      setName('');
      setShareLevel('private');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim() || saving) return;
    await onSave?.(name.trim(), shareLevel);
  };

  const handleClose = () => {
    setName('');
    setShareLevel('private');
    onClose?.();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ elevation: 3, sx: { p: 2, width: 320, borderRadius: 2 } }}>
      <Stack spacing={2}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={0.75}>
          <BookmarkAddIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
        </Box>

        {/* Name */}
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          label="Name"
          placeholder="e.g. High priority open bugs"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') handleClose();
          }}
          disabled={saving}
          error={!!error}
          helperText={error ?? undefined}
        />

        {/* Visibility */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            mb={0.75}
            display="block">
            Visibility
          </Typography>
          <Box display="grid" gridTemplateRows="repeat(2, 1fr)" gap={0.75}>
            {SHARE_OPTIONS.map(
              ({ value, label, description, disabledDescription, Icon }) => {
                const isOrgOption = value === 'organization';
                const isDisabled = isOrgOption && !canShareOrg;
                const isSelected = shareLevel === value;

                const card = (
                  <Box
                    key={value}
                    onClick={() =>
                      !isDisabled && !saving && setShareLevel(value)
                    }
                    sx={{
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      px: 1.25,
                      py: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.375,
                      cursor: isDisabled || saving ? 'default' : 'pointer',
                      opacity: isDisabled ? 0.45 : 1,
                      bgcolor: isSelected ? 'primary.light' : 'transparent',
                      // primary.light is always defined; adjust alpha below
                      ...(isSelected && {
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(99,102,241,0.15)'
                            : 'rgba(99,102,241,0.08)',
                      }),
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover':
                        !isDisabled && !saving && !isSelected
                          ? {
                              borderColor: 'text.disabled',
                              bgcolor: 'action.hover',
                            }
                          : {},
                    }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Icon
                        sx={{
                          fontSize: 13,
                          flexShrink: 0,
                          color: isSelected ? 'primary.main' : 'text.secondary',
                        }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        lineHeight={1.2}
                        color={isSelected ? 'primary.main' : 'text.primary'}>
                        {label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      lineHeight={1.3}
                      sx={{ fontSize: '0.68rem' }}>
                      {description}
                    </Typography>
                  </Box>
                );

                return isDisabled ? (
                  <Tooltip
                    key={value}
                    title={disabledDescription ?? ''}
                    placement="bottom"
                    arrow>
                    <span>{card}</span>
                  </Tooltip>
                ) : (
                  card
                );
              },
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="text"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!name.trim() || saving}
            startIcon={
              saving ? <CircularProgress size={13} color="inherit" /> : null
            }>
            Save
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default SavePresetPopover;
